import { useMutation } from '@tanstack/react-query';
import { postFormText } from '@/shared/api/api-client';

/**
 * POST /api/ConsentTemplate/UploadPdf (multipart) → stored blob URL (plain string).
 *
 * Contract: Scrips.Patient `ConsentTemplateController.UploadPdf` →
 * `IConsentDocumentStore.UploadTemplatePdfAsync` (PR #884). Uploads the blank clinic
 * PDF to the dedicated `consent-templates` container (PDF-only + %PDF magic-byte +
 * 15 MB guards on the server) and returns the blob URL the builder sends back as
 * `uploadedPdfUrl` on save.
 *
 * The endpoint is `Ok(string)`, so the body is a JSON string LITERAL — `"https://…"`
 * with literal quotes. Reading it as raw text (`postFormText`) keeps those quotes, and
 * persisting them poisons `uploadedPdfUrl`: the server's `new Uri(url)` then throws
 * "URI scheme is not valid" and `/pdf` 404s on an otherwise-valid blob (PROD-1051). Decode
 * the JSON literal (fallback: strip surrounding quotes) so we always store a clean URL.
 */
export function useUploadConsentTemplatePdf() {
  return useMutation<string, Error, File>({
    mutationFn: async (file) => {
      const form = new FormData();
      form.append('file', file, file.name);
      const raw = await postFormText('/api/ConsentTemplate/UploadPdf', form);
      try {
        const parsed = JSON.parse(raw);
        return typeof parsed === 'string' ? parsed : raw;
      } catch {
        return raw.replace(/^\s*"+|"+\s*$/g, '').trim();
      }
    },
  });
}
